import React, { useEffect, useState, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import emojiRegex from "emoji-regex";

import { Box, Button, Typography } from "@mui/material";

import UpdateDrawer from "components/globals/UpdateDrawer";
import {
  EmailInputWithSmartKeys,
  SelectionDropdown,
  SmsInputWithSmartKeys,
} from "./components";
import { grayDark } from "design-system/colors";
import {
  LOAD_NOTIFICATION_SETTINGS_QUERY,
  UPDATE_NOTIFICATION_SETTING_MUTATION,
} from "globals/graphql";
import { useSnackbar } from "globals/hooks";
import { NotificationChannel } from "types";

const EMOJI_REGEX = emojiRegex();

function UpdateNotificationSettingDrawer() {
  // hooks
  const history = useHistory();
  const { notificationSettingId } = useParams<{
    notificationSettingId: string;
  }>();
  const snackbar = useSnackbar();

  // state
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [smsContent, setSmsContent] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailDesign, setEmailDesign] = useState<any>(null);
  const [emailHtml, setEmailHtml] = useState<string>("");
  const [saveIndicatorState, setSaveIndicatorState] = useState<
    "default" | "loading" | "saved" | "error"
  >("default");

  // refs
  const fromEditorRef = useRef(false);

  // query
  const { data, loading } = useQuery(LOAD_NOTIFICATION_SETTINGS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const [
    updateNotificationSetting,
    { loading: updateNotificationSettingLoading },
  ] = useMutation(UPDATE_NOTIFICATION_SETTING_MUTATION, {
    onCompleted(data) {
      const result = data.updateNotificationSetting.result;

      if (result.__typename === "UpdateNotificationSettingFailure") {
        snackbar.error(result.reason);
        setSaveIndicatorState("error");
        return;
      }

      setSaveIndicatorState("saved");
      snackbar.success("Notification setting updated successfully");

      // Only navigate away if not from editor
      if (!fromEditorRef.current) {
        history.push("/settings/notifications");
      } else {
        // Reset save indicator after delay for editor saves
        setTimeout(() => {
          setSaveIndicatorState("default");
        }, 2000);
      }

      // Reset the ref
      fromEditorRef.current = false;
    },
    onError() {
      snackbar.error("Error updating notification setting");
      setSaveIndicatorState("error");
      // Reset the ref on error too
      fromEditorRef.current = false;
    },
  });

  const notification = data?.loadNotificationSettings?.find(
    (notification) => notification.id === notificationSettingId
  );

  // derived data
  const isEmail = notification?.channel === NotificationChannel.Email;

  // event handlers
  const handleCloseUpdateDrawer = () => {
    history.push("/settings/notifications");
  };

  const handleSmsContentChange = (e) => {
    let value = e.target.value;

    if (EMOJI_REGEX.test(value)) {
      snackbar.error("Emojis are not allowed in SMS content", {
        autoHideDuration: 5000,
      });
      value = value.replace(EMOJI_REGEX, ""); // ensure emoji is removed
    }

    setSmsContent(value);
  };

  const handleSmsSubmit = () => {
    // insert \n into smsContent for db
    const formattedText = smsContent.replace(/\n/g, "\\n");

    setSaveIndicatorState("loading");
    updateNotificationSetting({
      variables: {
        input: {
          id: notificationSettingId,
          templateContent: formattedText,
        },
      },
    });
  };

  const handleEmailSubjectChange = (e) => {
    setEmailSubject(e.target.value);
  };

  const handleEmailSubmit = (
    design?: any,
    html?: string,
    fromEditor = false
  ) => {
    // Set the ref to track save source
    fromEditorRef.current = fromEditor;

    // Update state if from editor
    if (fromEditor && design && html) {
      setEmailDesign(design);
      setEmailHtml(html);
    }

    // Use only passed values or state values, with default design fallback
    const finalDesign = design || emailDesign || { useDefault: true };
    const finalHtml = html || emailHtml;

    setSaveIndicatorState("loading");
    updateNotificationSetting({
      variables: {
        input: {
          id: notificationSettingId,
          templateContent: finalHtml,
          emailDesignJson: finalDesign,
          templateEmailSubject: emailSubject,
        },
      },
    });
  };

  // effects
  useEffect(() => {
    setSelectedRecipients(notification?.recipientType);
  }, [notification]);

  useEffect(() => {
    setSelectedChannels([notification?.channel]);
  }, [notification]);

  useEffect(() => {
    if (notification?.templateContent && !isEmail) {
      // Convert literal '\n' strings into actual newline characters
      const formattedContent = notification?.templateContent.replace(
        /\\n/g,
        "\n"
      );

      setSmsContent(formattedContent);
    }
  }, [notification?.templateContent, isEmail]);

  useEffect(() => {
    if (notification) {
      if (notification.templateEmailSubject) {
        setEmailSubject(notification.templateEmailSubject);
      }
      setEmailDesign(notification.emailDesignJson || { useDefault: true });
      if (notification.channel === NotificationChannel.Email) {
        setEmailHtml(notification.templateContent);
      }
    }
  }, [notification]);

  if (loading || !notification || !selectedRecipients || !selectedChannels) {
    return null;
  }

  return (
    <UpdateDrawer
      onClose={handleCloseUpdateDrawer}
      saveIndicatorState={saveIndicatorState}
      footerContent={
        <Box mr={5}>
          <Button
            variant="contained"
            color="primary"
            onClick={isEmail ? () => handleEmailSubmit() : handleSmsSubmit}
            disabled={updateNotificationSettingLoading}
          >
            Save
          </Button>
        </Box>
      }
    >
      <Box mt={3} mb={2}>
        <Typography variant="h3">{notification.templateName}</Typography>
      </Box>
      {/* recipient selection section */}
      {/* hiding recipient selection for now for v0 */}
      {/* <Box display="flex" gap={2}>
        <Box flex={1} display="flex" alignItems="center">
          <Typography variant="body2" color={grayDark}>
            Recipient
          </Typography>
        </Box>
        <Box flex={4}>
          <SelectionDropdown
            setSelected={setSelectedRecipients}
            selected={selectedRecipients}
            options={notification.recipientType}
            selectId="recipient-multiple-id"
          />
        </Box>
      </Box> */}

      {/* channel selection section */}
      <Box display="flex" gap={2}>
        <Box flex={1} display="flex" alignItems="center">
          <Typography variant="body2" color={grayDark}>
            Channel
          </Typography>
        </Box>
        <Box flex={4}>
          <SelectionDropdown
            setSelected={setSelectedChannels}
            selected={selectedChannels}
            options={[notification.channel]}
            selectId="channel-multiple-id"
          />
        </Box>
      </Box>

      {/* sms template section */}
      <Box my={3}>
        {isEmail ? (
          <EmailInputWithSmartKeys
            notificationSetting={notification}
            emailSubject={emailSubject}
            handleEmailSubjectChange={handleEmailSubjectChange}
            handleEmailSubmit={handleEmailSubmit}
            availableVariables={notification.event.availableVariables}
          />
        ) : (
          <SmsInputWithSmartKeys
            smsContent={smsContent}
            handleSmsContentChange={handleSmsContentChange}
            availableVariables={notification.event.availableVariables}
          />
        )}
      </Box>
    </UpdateDrawer>
  );
}

export default UpdateNotificationSettingDrawer;
